/**
 * Shared UX patterns for the portals (plan 22 phase 2, ADR 126 D14–D16):
 * one way to show a load, an error, an empty list, an undoable action, a
 * confirmation, a paged list, a form's problems and a page's focus.
 */
import {
  Badge,
  Button,
  Busy,
  Dialog,
  ErrorState,
  Icon,
  LoadMore,
  Skeleton,
  useToast,
  type IconName,
  type Tone,
} from '@laura/design-system';
import { useInfiniteQuery, type QueryKey } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ApiError } from './api.js';

// --- Loading, error and refetching ------------------------------------------

export interface QueryLike<T> {
  data: T | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  isFetching?: boolean;
  refetch: () => unknown;
}

export type SkeletonKind = 'text' | 'card' | 'table' | 'list';

function skeletonFor(kind: SkeletonKind, lines: number): ReactNode {
  if (kind === 'card') return <Skeleton kind="card" />;
  if (kind === 'table' || kind === 'list')
    return (
      <div className="skeleton-list" aria-hidden="true">
        {Array.from({ length: lines }, (_, i) => (
          <Skeleton kind="table-row" key={i} />
        ))}
      </div>
    );
  return <Skeleton kind="text" lines={lines} />;
}

/** What a load error says: the API's own code where it means something, else plain words. */
export function loadErrorText(error: unknown, what: string): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return `You do not have access to ${what}.`;
    if (error.status === 404)
      return `${what[0]?.toUpperCase()}${what.slice(1)} could not be found.`;
    if (error.status >= 500) return 'The server had a problem. It is usually brief.';
  }
  if (error instanceof TypeError) return 'Check your internet connection.';
  return `${what[0]?.toUpperCase()}${what.slice(1)} could not be loaded.`;
}

/**
 * Renders one query's three states. While loading, a skeleton in the shape of
 * what is coming; on failure, the cause and a retry; while refetching, the old
 * content stays and the region is marked busy (never a false empty state).
 */
export function QueryState<T>({
  query,
  what,
  skeleton = 'text',
  lines = 3,
  children,
}: {
  query: QueryLike<T>;
  /** Named in the error: "tasks", "the dashboard". */
  what: string;
  skeleton?: SkeletonKind;
  lines?: number;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <Busy busy>{skeletonFor(skeleton, lines)}</Busy>;
  if (query.isError || query.data === undefined)
    return (
      <ErrorState
        title={`${what[0]?.toUpperCase()}${what.slice(1)} could not be loaded`}
        onRetry={() => void query.refetch()}
      >
        {loadErrorText(query.error, what)}
      </ErrorState>
    );
  return <Busy busy={Boolean(query.isFetching)}>{children(query.data)}</Busy>;
}

// --- Undo -------------------------------------------------------------------

export const UNDO_WINDOW_MS = 6000;
let undoWindowMs = UNDO_WINDOW_MS;

/** Tests shorten the window so a held action commits at once. */
export function configureUndo({ windowMs }: { windowMs: number }): void {
  undoWindowMs = windowMs;
}

export interface UndoableOptions {
  /** Toast text, e.g. "Product retired." */
  message: string;
  /** Runs when the window closes without an undo (client-side undo). */
  commit: () => Promise<unknown> | unknown;
  /** Runs at once when given; then `undo` reverts it (server-side undo). */
  immediate?: boolean;
  undo?: () => Promise<unknown> | unknown;
  /** Called after the commit ran or the undo was chosen. */
  onSettled?: (undone: boolean) => void;
  undoLabel?: string;
}

/**
 * An action the person can take back for a few seconds (D15): the toast
 * carries Undo and stays until used or closed. Without `immediate`, the call
 * is held until the window closes, so nothing changes if they change their mind.
 */
export function useUndo() {
  const toast = useToast();
  const pending = useRef(new Map<ReturnType<typeof setTimeout>, () => void>());
  useEffect(() => {
    const held = pending.current;
    return () => {
      // Leaving the page settles every held action: a delayed change must not vanish.
      for (const [t, settle] of held) {
        clearTimeout(t);
        settle();
      }
      held.clear();
    };
  }, []);
  return useCallback(
    (options: UndoableOptions) => {
      let undone = false;
      let done = false;
      const settle = () => {
        if (done) return;
        done = true;
        void Promise.resolve(options.commit()).then(() => options.onSettled?.(false));
      };
      if (options.immediate) {
        void Promise.resolve(options.commit());
        toast(options.message, {
          action: {
            label: options.undoLabel ?? 'Undo',
            onAction: () => {
              undone = true;
              void Promise.resolve(options.undo?.()).then(() => options.onSettled?.(true));
            },
          },
          // ADR 248: the toast leaves when the undo window ends (never shorter than the
          // real window, so a test's quick window does not hide the message).
          duration: Math.max(undoWindowMs, UNDO_WINDOW_MS),
        });
        return;
      }
      const timer = setTimeout(() => {
        pending.current.delete(timer);
        if (!undone) settle();
      }, undoWindowMs);
      pending.current.set(timer, settle);
      toast(options.message, {
        action: {
          label: options.undoLabel ?? 'Undo',
          onAction: () => {
            undone = true;
            clearTimeout(timer);
            pending.current.delete(timer);
            options.onSettled?.(true);
          },
        },
        duration: Math.max(undoWindowMs, UNDO_WINDOW_MS),
      });
    },
    [toast],
  );
}

// --- Confirmation -----------------------------------------------------------

export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  /** Names the outcome: "Deactivate Sam", never "Yes". */
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
}

type Ask = (options: ConfirmOptions) => Promise<boolean>;
const ConfirmContext = createContext<Ask>(() => Promise.resolve(false));

/**
 * One confirmation dialog for the app (D15): for the irreversible only. The
 * dialog restates what will happen and names both outcomes.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ options: ConfirmOptions; resolve: (v: boolean) => void }>();
  const ask = useCallback<Ask>(
    (options) => new Promise<boolean>((resolve) => setState({ options, resolve })),
    [],
  );
  const close = (value: boolean) => {
    state?.resolve(value);
    setState(undefined);
  };
  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      {state && (
        <Dialog
          open
          title={state.options.title}
          onClose={() => close(false)}
          footer={
            <>
              <Button onClick={() => close(false)}>{state.options.cancelLabel ?? 'Cancel'}</Button>
              <Button
                variant={state.options.danger ? 'danger' : 'primary'}
                onClick={() => close(true)}
              >
                {state.options.confirmLabel}
              </Button>
            </>
          }
        >
          {state.options.body}
        </Dialog>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): Ask {
  return useContext(ConfirmContext);
}

// --- URL state --------------------------------------------------------------

/**
 * A filter, a view or a search kept in the URL, so the page can be shared and
 * Back brings it back as it was. Empty and default values leave the URL clean.
 */
export function useUrlState<T extends string>(
  key: string,
  fallback: T,
  allowed?: readonly T[],
): [T, (value: T) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);
  const value =
    raw !== null && (!allowed || (allowed as readonly string[]).includes(raw))
      ? (raw as T)
      : fallback;
  const set = useCallback(
    (next: T) => {
      setParams(
        (p) => {
          const copy = new URLSearchParams(p);
          if (next === fallback || next === '') copy.delete(key);
          else copy.set(key, next);
          return copy;
        },
        { replace: true },
      );
    },
    [fallback, key, setParams],
  );
  return [value, set];
}

// --- Paged lists ------------------------------------------------------------

export interface Page<T> {
  items: T[];
  cursor: string | null;
}

/** A list the API pages by cursor, flattened, with "load more". */
export function usePagedQuery<T>(
  queryKey: QueryKey,
  fetchPage: (cursor: string | null) => Promise<Page<T>>,
  options: { enabled?: boolean } = {},
) {
  const q = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage(pageParam as string | null),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<T>) => last.cursor ?? undefined,
    enabled: options.enabled ?? true,
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  return {
    items,
    isPending: q.isPending,
    isError: q.isError,
    error: q.error,
    isFetching: q.isFetching,
    refetch: q.refetch,
    data: q.data ? items : undefined,
    hasMore: Boolean(q.hasNextPage),
    loadingMore: q.isFetchingNextPage,
    loadMore: () => void q.fetchNextPage(),
    /** The first page as the API sent it, for extra fields beside the items (Inbox counts). */
    firstPage: q.data?.pages[0],
  };
}

export function PagedFooter({
  paged,
  noun,
}: {
  paged: { items: unknown[]; hasMore: boolean; loadingMore: boolean; loadMore: () => void };
  noun: string;
}) {
  if (paged.items.length === 0) return null;
  return (
    <LoadMore
      shown={paged.items.length}
      hasMore={paged.hasMore}
      loading={paged.loadingMore}
      onMore={paged.loadMore}
      noun={noun}
    />
  );
}

// --- Forms ------------------------------------------------------------------

export interface Problem {
  field: string;
  message: string;
}

/**
 * A form's problems, GOV.UK style (D15): checked on submit, listed at the top
 * (ErrorSummary focuses itself), each one also shown by its field, the page
 * title prefixed while they stand, and the first field focused.
 */
export function useFormProblems() {
  const [problems, setProblems] = useState<Problem[]>([]);
  useEffect(() => {
    if (problems.length === 0) return;
    const title = document.title;
    if (!title.startsWith('Error: ')) document.title = `Error: ${title}`;
    return () => {
      if (document.title.startsWith('Error: ')) document.title = document.title.slice(7);
    };
  }, [problems]);
  const clear = useCallback(() => setProblems([]), []);
  // The same list when the field had no problem, so the summary does not take focus
  // again on every keystroke while another problem is still shown.
  const clearField = useCallback(
    (field: string) =>
      setProblems((all) =>
        all.some((p) => p.field === field) ? all.filter((p) => p.field !== field) : all,
      ),
    [],
  );
  const messageFor = useCallback(
    (field: string) => problems.find((p) => p.field === field)?.message,
    [problems],
  );
  /** Runs `validate`; false and the problems shown when it finds any. */
  const check = useCallback((validate: () => Problem[]) => {
    const found = validate();
    setProblems(found);
    if (found.length > 0) {
      // ErrorSummary takes focus first; the first field is one Tab away, and gets
      // focus directly when the summary is not rendered.
      setTimeout(() => {
        if (!document.querySelector('.errsum'))
          document.getElementById(found[0]?.field ?? '')?.focus();
      }, 0);
      return false;
    }
    return true;
  }, []);
  /** Maps a 400/422 body `{error: {fields: {name: "message"}}}` to problems; false when it is not that shape. */
  const fromServer = useCallback((error: unknown, known: string[]): boolean => {
    if (!(error instanceof ApiError) || (error.status !== 400 && error.status !== 422))
      return false;
    const body = error.body as { error?: { fields?: Record<string, string> } } | undefined;
    const fields = body?.error?.fields;
    if (!fields) return false;
    const found = Object.entries(fields)
      .filter(([k]) => known.includes(k))
      .map(([field, message]) => ({ field, message }));
    if (found.length === 0) return false;
    setProblems(found);
    return true;
  }, []);
  return { problems, setProblems, clear, clearField, messageFor, check, fromServer };
}

/**
 * Warns before the tab closes or reloads while a form has unsaved changes
 * (D15). In-app navigation keeps the SaveBar in view, so it is not blocked.
 */
export function useUnsavedChanges(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Older browsers need a value to show their prompt.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
}

// --- Route focus and title ----------------------------------------------------

/**
 * On every route change the page heading receives focus and the document title
 * names the page (WCAG 2.4.2, 2.4.3): a screen reader hears where it landed.
 * Nothing moves on the first render, so the sign-in field keeps focus.
 */
export function useRouteFocus(product: string) {
  const location = useLocation();
  const first = useRef(true);
  useEffect(() => {
    const h1 = document.querySelector<HTMLHeadingElement>('main h1');
    const page = h1?.textContent?.trim();
    document.title = page ? `${page} · ${product}` : product;
    if (first.current) {
      first.current = false;
      return;
    }
    if (h1) {
      if (!h1.hasAttribute('tabindex')) h1.setAttribute('tabindex', '-1');
      h1.focus({ preventScroll: false });
    }
  }, [location.pathname, product]);
}

// --- Status badges ------------------------------------------------------------

/** A state as icon + text + colour, never colour alone (D14, WCAG 1.4.1). */
export function StatusBadge({
  tone,
  icon,
  children,
}: {
  tone: Tone;
  icon?: IconName;
  children: ReactNode;
}) {
  const name: IconName | undefined =
    icon ??
    (tone === 'green'
      ? 'check'
      : tone === 'red'
        ? 'warning'
        : tone === 'amber'
          ? 'info'
          : undefined);
  return (
    <Badge tone={tone}>
      {name && <Icon name={name} size={16} />}
      {children}
    </Badge>
  );
}
